import { useEffect, useRef, useState } from 'react';

interface Options {
  /** Espera antes de mostrar: una respuesta más rápida que esto no enseña nada. */
  delayMs?: number;
  /** Una vez mostrado, se mantiene al menos este tiempo para que no desaparezca en un fotograma. */
  minVisibleMs?: number;
}

/**
 * Convierte un booleano de carga en uno "visible": aparece solo si la carga dura más que `delayMs` y, una
 * vez visible, se queda al menos `minVisibleMs`. Es lo que evita el parpadeo de spinners y skeletons en
 * respuestas rápidas.
 */
export const useDelayedFlag = (active: boolean, { delayMs = 150, minVisibleMs = 300 }: Options = {}): boolean => {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (active) {
      timer = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, delayMs);
    } else if (shownAt.current !== null) {
      const remaining = Math.max(0, minVisibleMs - (Date.now() - shownAt.current));
      timer = setTimeout(() => {
        shownAt.current = null;
        setVisible(false);
      }, remaining);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timer);
  }, [active, delayMs, minVisibleMs]);

  return visible;
};
